import type {
  ChecklistItemResponse,
  LifecycleChecklistListResponse,
  OffboardRequest,
  OffboardResponse,
  OffboardingPreviewResponse,
  OnboardingRequest,
  OnboardingResponse,
  TickChecklistItemRequest,
} from '@accounts/shared';

import { postForText, requestJson } from './api';

function postJson<T>(path: string, body?: unknown): Promise<T> {
  return requestJson<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export function onboardUser(body: OnboardingRequest): Promise<OnboardingResponse> {
  return postJson<OnboardingResponse>('/api/admin/lifecycle/onboarding', body);
}

export function getOffboardingPreview(userId: string): Promise<OffboardingPreviewResponse> {
  return requestJson<OffboardingPreviewResponse>(
    `/api/admin/users/${encodeURIComponent(userId)}/offboarding`,
  );
}

export function offboardUser(userId: string, body: OffboardRequest): Promise<OffboardResponse> {
  return postJson<OffboardResponse>(
    `/api/admin/users/${encodeURIComponent(userId)}/offboard`,
    body,
  );
}

export function listLifecycleChecklists(params: {
  userId: string;
  kind: 'onboarding' | 'offboarding';
  state?: 'open' | 'completed' | 'retired' | 'all';
}): Promise<LifecycleChecklistListResponse> {
  const search = new URLSearchParams({ userId: params.userId, kind: params.kind });
  if (params.state !== undefined) search.set('state', params.state);
  return requestJson<LifecycleChecklistListResponse>(
    `/api/admin/lifecycle/checklists?${search.toString()}`,
  );
}

export function tickChecklistItem(
  id: string,
  body: TickChecklistItemRequest = {},
): Promise<ChecklistItemResponse> {
  return postJson<ChecklistItemResponse>(
    `/api/admin/lifecycle/items/${encodeURIComponent(id)}/tick`,
    body,
  );
}

export function reopenChecklistItem(id: string): Promise<ChecklistItemResponse> {
  return postJson<ChecklistItemResponse>(
    `/api/admin/lifecycle/items/${encodeURIComponent(id)}/reopen`,
  );
}

/**
 * Draft IT email for one checklist (spec row 7): the server's TEXT, grouped Set up / Remove
 * and then by system, each line saying what to do. Audited; 409 when nothing is open for IT.
 */
export function draftChecklistItEmail(checklistId: string): Promise<string> {
  return postForText(`/api/admin/lifecycle/checklists/${encodeURIComponent(checklistId)}/it-email`);
}
